import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../utils/apiConfig';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export default function UnsubscribePage(): React.ReactElement {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useDocumentMeta({
    title: `${t('unsubscribe.title')} — Heseda AI`,
    description: 'Manage your Heseda AI email notification preferences or unsubscribe.',
    path: '/unsubscribe',
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const disabled = useMemo(() => !email || status === 'loading', [email, status]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${getApiBaseUrl()}/unsubscribeEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to unsubscribe');
      }

      setStatus('success');
      setMessage(data?.message || t('unsubscribe.successMessage'));
    } catch (error: any) {
      setStatus('error');
      setMessage(error?.message || t('unsubscribe.errorMessage'));
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      minHeight: CSS.supports?.('min-height', '100dvh') ? '100dvh' : '100vh',
      background: 'linear-gradient(135deg, #ece9e6, #ffffff)',
      padding: '20px', boxSizing: 'border-box',
    }}>
     <div style={{ maxWidth: 480, width: '100%', padding: '32px', background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', boxSizing: 'border-box' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <img src="/logo-512-colorful.png" alt="Heseda AI" style={{ width: 56, height: 56 }} />
      </div>
      <h1 style={{ marginTop: 0, marginBottom: 12, textAlign: 'center' }}>{t('unsubscribe.heading')}</h1>
      <p style={{ marginTop: 0, color: '#444' }}>
        {t('unsubscribe.description')}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ fontWeight: 600 }} htmlFor="email">{t('unsubscribe.emailLabel')}</label>
        <input
          id="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid #d0d5dd', fontSize: 15 }}
          placeholder="you@example.com"
        />
        <button
          type="submit"
          disabled={disabled}
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            border: 'none',
            background: disabled ? '#c7c9d1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            fontWeight: 700,
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
        >
          {status === 'loading' ? t('unsubscribe.processing') : t('unsubscribe.updatePreferences')}
        </button>
      </form>

      {message && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: status === 'success' ? '#ecfdf3' : '#fff4ed', color: status === 'success' ? '#027a48' : '#b93815' }}>
          {message}
        </div>
      )}

      <p style={{ marginTop: 16, color: '#666', fontSize: 13 }}>
        <Trans i18nKey="unsubscribe.helpText" ns="common">
          Need help? Contact us at <a href="mailto:dev@heseda.com" style={{ color: '#667eea' }}>dev@heseda.com</a>
        </Trans>
      </p>
     </div>
    </div>
  );
}
